/**
 * @file Portfolio.jsx
 * @description Displays and manages user's investment portfolio with performance metrics
 *
 * @section Responsibilities
 * - Display portfolio holdings
 * - Show performance metrics
 * - Handle time-based filtering
 * - Manage portfolio updates
 * - Display asset allocation
 * - Show transaction history
 *
 * @section Dependencies
 * - TimeFilterContext: Time-based filtering
 * - Portfolio Services: Data management
 * - Charting Library: Performance visualization
 * - Asset Management: Holding details
 *
 * @section Integration Points
 * - Portfolio API: Holding information
 * - Transaction Service: Trade history
 * - Performance Service: Metrics calculation
 * - Asset Service: Security details
 * - Time Filter: Date range selection
 *
 * @section State Management
 * - portfolioData: Holding information
 * - performanceData: Metrics and charts
 * - timeFilter: Date range selection
 * - loadingState: Data fetching status
 * - errorState: Error handling
 *
 * @section Events
 * - onTimeFilterChange: Date range updates
 * - onPortfolioUpdate: Holding changes
 * - onError: Error handling
 * - onLoading: Loading state changes
 *
 * @section Props
 * @prop {Object} initialData - Initial portfolio data
 * @prop {Function} onError - Error handling callback
 * @prop {Boolean} autoRefresh - Controls automatic updates
 * @prop {Object} timeRange - Default time filter
 */

import React, { useCallback, useEffect, useState } from "react";
import SkinPage from "../../SkinPage";
import Widget from "../../widgets/Widget";
import PersonalPortfolio from "../../widgets/portfolio/PersonalPortfolio";
import PortfolioAllocation from "../../widgets/portfolio/PortfolioAllocation";
import Request from "../../../../utils/fetchUtils";
import { getFromSession } from "../../../../utils/sessionUtils";
import { useTimeFilter } from "../../../../contexts/TimeFilterContext";
import StrategiesGainLoss from "../../widgets/portfolio/StrategiesGainLoss";
import PositionsComposition from "../../widgets/portfolio/PositionsComposition";
import Loader from "../../widgets/Loader";

function Portfolio() {
  const brokerAccounts = getFromSession("brokerAccounts") ?? [];
  const [currentAccount, setCurrentAccount] = useState(brokerAccounts[0] || {});

  const [isLoading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [strategies, setStrategies] = useState([]);
  const [fetchedStrategies, setFetchedStrategies] = useState(false);

  const { timeQuery } = useTimeFilter();

  const advisor = {
    name: getFromSession("advisorName") ?? "",
    phone: getFromSession("advisorPhone") ?? "",
  };

  const getDashboardData = useCallback(async () => {
    setLoading(true);
    if (currentAccount.id) {
      const res = await Request(
        `/trading/dashboard?accountId=${currentAccount.id}&${timeQuery}`
      );
      if (res.ok) {
        setData(res.data);

        if (res.data.strategies.length > 0 && !fetchedStrategies) {
          getStrategiesDetails(res.data.strategies);
        } else {
          setLoading(false);
        }
      }
    }
  });

  const getStrategiesDetails = useCallback(async (stragiesInfo) => {
    let tmpStrategies = [];
    setFetchedStrategies(true);

    const promises = stragiesInfo.map(async (strat) => {
      const res = await Request(`/trading/strategy/${strat.strategyId}`);

      if (res.ok) {
        tmpStrategies.push(res.data);
      }
    });

    await Promise.all(promises);

    setLoading(false);
    setStrategies(tmpStrategies);
  });

  useEffect(() => {
    getDashboardData();
  }, [timeQuery]);

  return (
    <SkinPage>
      <PersonalPortfolio
        width={"full-width"}
        data={data}
        loading={isLoading}
        account={currentAccount}
      />
      <div className="widget-row">
        <div className="widget-column">
          <StrategiesGainLoss
            width={"full-width"}
            data={strategies}
            loading={isLoading}
            account={currentAccount}
          />
          <PortfolioAllocation
            width={"full-width"}
            data={data}
            loading={isLoading}
            account={currentAccount}
          />
        </div>
        <div className="widget-column">
          <PositionsComposition
            width={"full-width"}
            data={data.positions}
            loading={isLoading}
            account={currentAccount}
          />
        </div>
      </div>
      <div className="widget-row"></div>
      <div className="widget-row">
        {strategies?.map((strategy, index) => (
          <Widget key={index} width={"full-width"}>
            <div className="heading">
              <h1 className="title">{strategy.name}</h1>
            </div>
            <table className="basic-table">
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Cost basis</th>
                  <th>Qty</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <Loader />
                ) : (
                  strategy.positions.map((position) => (
                    <tr key={position.id}>
                      <td className="symbol">
                        <p className="order-text">{position.symbol}</p>
                      </td>
                      <td className="cost-basis">
                        <p className="order-text">${position.costBasis}</p>
                      </td>
                      <td className="qty">
                        <p className="order-text">{position.quantity}</p>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </Widget>
        ))}
      </div>
    </SkinPage>
  );
}

export default Portfolio;
